import React, { useEffect, useRef, useState, useCallback } from 'react';
import { Swiper, SwiperSlide } from 'swiper/react';
import { Navigation, Pagination, Mousewheel } from 'swiper/modules';
import type { Swiper as SwiperType } from 'swiper';

// Swiper styles
import 'swiper/css';
import 'swiper/css/navigation';
import 'swiper/css/pagination';

// Assuming your Redux setup and hooks are correct
import { useAppSelector } from '../store/hooks';

// --- Interface Definitions ---
interface VideoItem {
  id: string;
  snippet: {
    resourceId: {
      videoId: string;
    };
  };
}

interface YouTubeApiResponse {
  items: VideoItem[];
  nextPageToken?: string;
}

// --- Shorts Component ---
const Shorts: React.FC = () => {  

  // --- Redux State Selection ---
  const darkMode = useAppSelector((state: any) => state.categoryNavigation.darkmode);
  // Assuming headerHeight and fooNavHeight are in the 'ui' slice as per best practices.
  // If they are still in 'categoryNavigation', adjust these lines accordingly.
  const headerHeight = useAppSelector((state: any) => state.categoryNavigation.headerHeight || 0);
  const fooNavHeight = useAppSelector((state: any) => state.categoryNavigation.fooNavHeight || 0);

  // --- Component State and Refs ---
  const iframeRefs = useRef<(HTMLIFrameElement | null)[]>([]); // To store refs to iframes for control
  const [videos, setVideos] = useState<string[]>([]); // Array of video IDs
  const [nextPageToken, setNextPageToken] = useState<string | undefined>(undefined); // For YouTube API pagination
  const [activeIndex, setActiveIndex] = useState<number>(0); // Current active Swiper slide index
  const swiperRef = useRef<SwiperType | null>(null); // Ref to the Swiper instance

  // Environment variables for YouTube API
  const API_KEY = process.env.REACT_APP_YOUTUBE_API_KEY || '';
  const playlistId = process.env.REACT_APP_YOUTUBE_PLAYLIST_ID || '';

  // --- Data Fetching Logic ---
  const fetchVideos = useCallback(async (pageToken: string = '') => {
    if (!API_KEY || !playlistId) {
      console.error('YouTube API Key or Playlist ID is missing. Please check .env file.');
      return;
    }

    try {
      const response = await fetch(
        `https://www.googleapis.com/youtube/v3/playlistItems?part=snippet&maxResults=20&playlistId=${playlistId}&key=${API_KEY}&pageToken=${pageToken}`
      );

      if (!response.ok) {
        throw new Error(`HTTP error! status: ${response.status}`);
      }

      const data: YouTubeApiResponse = await response.json();

      if (data.items) {
        const videoIds = data.items.map(item => item.snippet.resourceId.videoId);
        setVideos(prev => [...prev, ...videoIds]);
        setNextPageToken(data.nextPageToken);
      }
    } catch (error) {
      console.error('Error fetching videos:', error);
    }
  }, [API_KEY, playlistId]); // Dependencies for useCallback

  // --- Initial Data Fetch & Height Calculation on Mount/Resize ---
  useEffect(() => {    
    fetchVideos();
  }, [fetchVideos]); 

  // --- Swiper Slide Change Handler ---
const handleSlideChange = useCallback(() => {
  if (!swiperRef.current) return;

  const currentIndex = swiperRef.current.activeIndex;

  // Pause all previous iframes
  iframeRefs.current.forEach((iframe, index) => {
    if (iframe && index !== currentIndex) {
      iframe.contentWindow?.postMessage(
        JSON.stringify({ event: 'command', func: 'pauseVideo', args: [] }),
        '*'
      );
    }
  });

  setActiveIndex(currentIndex);

  // Preload more if near end
  if (currentIndex >= videos.length - 5 && nextPageToken) {
    fetchVideos(nextPageToken);
  }
}, [videos, nextPageToken, fetchVideos]);


  useEffect(() => {
    document.body.style.overflow = 'hidden';
    const navMenu = document.querySelector('.navigation-menu');
    const headSelecter = document.querySelector('.main-header');
    
     const mainBlockSelecter = document.querySelector(".dynamic-pad-top") as HTMLElement | null;
  if (navMenu) {
    navMenu.classList.add('hidden');
  } 
  if(headSelecter && mainBlockSelecter){
    const headHeight = headSelecter.clientHeight; 
    mainBlockSelecter.style.paddingTop = `${headHeight}px`;

  }
  


    return () => {
      document.body.style.overflow = ''; // Resets to default (e.g., 'auto')
      if (navMenu) {
      navMenu.classList.remove('hidden');
    }
    };
  }, []);

const controlHeight = 200; // estimated height of YouTube's button area
const topOffset = (window.innerHeight - controlHeight) / 2;
const bottomOffset = topOffset + controlHeight;
    
  return (
    <div className={`font-sans min-h-screen ${darkMode ? 'bg-gray-900 text-white' : 'bg-gray-100 text-gray-900'}`}>
      <main
        className="dynamic-pad-top pt-24x pb-0 px-3 shorts-container mt-2" // Tailwind classes for padding, adjust if needed
        style={{
          height: '100vh', // Main content area takes full viewport height
          overflowY: 'hidden' // Main content area should not scroll independently
        }}       
      >
        {/* Container for Swiper with dynamic height */}
        <div
          style={{
            // This height is crucial for vertical mousewheel.
            // It calculates the remaining viewport height after header and footer.
            height: `calc(100vh - ${headerHeight + fooNavHeight + 5}px)`,
            minHeight: '200px', // Fallback minimum height in case Redux values are initially 0
            background: '#000',
            position: 'relative',
            display: 'flex', // Use flex to center the Swiper within this div
            justifyContent: 'center',
            alignItems: 'center',
            // Add border for debugging to see its boundaries:
            // border: '1px solid red', 
          }}
        >
          <Swiper
  onSwiper={(swiper: SwiperType) => (swiperRef.current = swiper)}
  direction="vertical"
  slidesPerView={1}
  spaceBetween={0}
  mousewheel={{
    forceToAxis: true,
    releaseOnEdges: true,
    sensitivity: 1,
  }}
  allowTouchMove={true}
  threshold={10}
  resistanceRatio={0}
  navigation={true}
  pagination={false}
  modules={[Navigation, Pagination, Mousewheel]}
  onSlideChangeTransitionEnd={handleSlideChange}
  className="yt-reels-swiper"
  style={{
    width: '100%',
    height: '100%',
    maxWidth: '480px',
  }}
>
            {/* Map through videos to create Swiper slides */}
            {videos.map((videoId, index) => (
              <SwiperSlide key={index}> {/* Use videoId as key for better uniqueness and React diffing */}
                <div style={{
                  height: '100%',
                  width: '100%',
                  position: 'relative',
                  background: '#000',
                  display: 'flex', // Center video/thumbnail within the slide
                  justifyContent: 'center',
                  alignItems: 'center',
                }}>
                  {/* Conditionally render iframe only if it's the active slide */}
                  {activeIndex === index && videoId ? (
                    <div style={{ width: '100%', height: '100%', position: 'relative' }}>
                    <iframe
                      ref={(el: HTMLIFrameElement | null) => {
                        // Store ref to the iframe element
                        iframeRefs.current[index] = el;
                      }}
                      // CORRECT YOUTUBE EMBED URL. Autoplay for the active slide.
                      // `enablejsapi=1` is crucial if you want to control player via JS later                  
                    src={`https://www.youtube.com/embed/${videoId}?rel=0&enablejsapi=1&autoplay=1&mute=0&playsinline=1`}    
                      frameBorder="0"                      
                      allow="accelerometer; autoplay; clipboard-write; encrypted-media; gyroscope; picture-in-picture"                      
                      allowFullScreen
                      style={{
                        width: '100%',
                        height: '100%',
                        objectFit: 'cover', // Ensures video fills the iframe area
                        position: 'absolute', // Allows full coverage
                        top: 0,
                        left: 0,
                        zIndex: 0,
                      }}
                      title={`YouTube Short ${index + 1}`}
                    ></iframe>
                     <div className="top-pull" style={{position: 'absolute',
                     top: 0,
                     left: 0,
                     right: 0,
                     zIndex: 1,
                     touchAction: 'pan-y',
                     opacity:0.3,
                     height: `${topOffset}px`,
                     }}></div>
                     <div className="bottom-pull" style={{
                    position: 'absolute',
                     bottom: 0,
                     left: 0,
                     right: 0,
                     zIndex: 1,
                     touchAction: 'pan-y',
                     height: `${window.innerHeight - bottomOffset}px`,
                     opacity:0.3}}></div>

{/* Swipeable area: left */}
<div className="left-pull" style={{
  position: 'absolute',
  top: 'calc(50% - 40px)', // Adjust to match play button vertical
  left: 0,
  bottom: 'calc(50% - 40px)',
  width: 'calc(50% - 45px)', // Adjust to match play button width
  zIndex: 2,
  touchAction: 'pan-y'  
}}></div>

{/* Swipeable area: right */}
<div className="right-pull" style={{
  position: 'absolute',
  top: 'calc(50% - 40px)',
  right: 0,
  bottom: 'calc(50% - 40px)',
  width: 'calc(50% - 40px)',
  zIndex: 2,
  touchAction: 'pan-y'  
}}></div>




    </div>
                  ) : (
                    // Display a thumbnail for inactive slides
                    <img
                      // CORRECT YOUTUBE THUMBNAIL URL
                      src={`https://img.youtube.com/vi/${videoId}/maxresdefault.jpg`} // High-res thumbnail
                      alt="Video thumbnail"
                      style={{
                        width: '100%',
                        height: '100%',
                        objectFit: 'cover'
                      }}
                      onError={(e: React.SyntheticEvent<HTMLImageElement, Event>) => {
                        // Fallback to a lower quality thumbnail if maxresdefault isn't found
                        e.currentTarget.src = `https://img.youtube.com/vi/${videoId}/mqdefault.jpg`; // Medium quality fallback
                      }}
                    />
                  )}
                </div>
              </SwiperSlide>
            ))}
          </Swiper>
        </div>
      </main>
    </div>
  );
};

export default Shorts;